import { Container, Graphics, Sprite, Text } from 'pixi.js';
import type { ScratchOffer, ScratchState, ScratchTile } from '@goodtime/shared';
import { AudioBus } from '../../audio/AudioBus';
import { canScratch, scratchLead, scratchTileExplain, tileAt, winningTiles } from '../../logic/minigames/scratch';
import { GAME_SOUNDS } from '../../logic/minigames/sounds';
import { BoardPanel } from '../board/BoardPanel';
import { animate, COLOURS, displayStyle, uiStyle } from '../board/ui';
import { glowTexture } from '../road/glow';
import { gridCells } from './pieces';
import { TurnGameDialog } from './TurnGameDialog';
import type { MiniGameDeps } from './types';
import type { GameView } from '../../logic/minigames/core';

interface TileView {
  root: Container;
  /** Under the foil: a small board, lit gold for the winning three. */
  well: BoardPanel;
  glow: Sprite;
  ring: Graphics;
  picture: Sprite;
  value: Text;
  foil: Container;
  foilArt: Sprite;
  foilDrawn: Graphics;
  foilWord: Text;
  shown: boolean;
}

/**
 * Scratch card: nine foil tiles over the slot's pictures, each picture
 * worth its prize. Tap a tile to scratch it, or scratch them all; three of
 * a picture win it. A card without three says so plainly. Once it ends,
 * the whole card shows, and a win lights its three. Tapping an uncovered
 * tile says what three of it pay.
 */
export class ScratchDialog extends TurnGameDialog<ScratchOffer, ScratchState, ScratchTile[]> {
  private tiles: TileView[] = [];

  constructor(deps: MiniGameDeps) {
    super(deps, 'scratch', 'SCRATCH CARD');
  }

  protected begin(offer: ScratchOffer): void {
    this.tiles.forEach((t) => t.root.destroy({ children: true }));
    const n = offer.setup?.tiles ?? 9;
    this.tiles = Array.from({ length: n }, (_, i) => {
      const root = new Container();
      const well = new BoardPanel({ floor: this.deps.floor(), radius: 10 });
      const glow = new Sprite(glowTexture());
      glow.anchor.set(0.5);
      glow.blendMode = 'add';
      glow.tint = 0xffd36b;
      glow.visible = false;
      const ring = new Graphics();
      const picture = new Sprite();
      picture.anchor.set(0.5);
      const value = new Text({ text: '', style: displayStyle(14) });
      value.anchor.set(0.5);
      const foil = new Container();
      const foilArt = new Sprite();
      foilArt.anchor.set(0.5);
      const foilDrawn = new Graphics();
      const foilWord = new Text({ text: 'SCRATCH', style: uiStyle(12, 0x4a4a52, '700') });
      foilWord.style.stroke = { color: 0xffffff, width: 2 };
      foilWord.anchor.set(0.5);
      foil.addChild(foilDrawn, foilArt, foilWord);
      root.addChild(glow, well, ring, picture, value, foil);
      root.eventMode = 'static';
      root.cursor = 'pointer';
      root.on('pointertap', () => this.onPiece(i));
      this.content.addChild(root);
      return { root, well, glow, ring, picture, value, foil, foilArt, foilDrawn, foilWord, shown: false };
    });
  }

  protected onPiece(index: number): void {
    const picture = tileAt(this.game, index);
    const tile = this.tiles[index];
    if (picture !== null && tile) {
      const coins = this.offer?.setup?.values?.[picture] ?? 0;
      this.explain(scratchTileExplain(coins), { x: tile.root.x, y: tile.root.y - tile.well.boardHeight / 2 });
      return;
    }
    if (!canScratch(this.game, index)) return;
    this.act('scratch', String(index), index);
  }

  protected override sideLabel(): string | null {
    return 'Scratch them all';
  }

  protected override onSideTap(): void {
    this.act('all');
  }

  protected override onAnswered(_prev: GameView<ScratchState, ScratchTile[]>, next: GameView<ScratchState, ScratchTile[]>): void {
    if (next.last?.length) AudioBus.shared.play(GAME_SOUNDS.scratch, { volume: 0.8 });
  }

  protected leadText(): string {
    return scratchLead(this.game);
  }

  protected pieceLabels(): string[] {
    return this.tiles.map((_, i) => {
      const p = tileAt(this.game, i);
      return p === null ? `Tile ${i + 1}` : `Tile ${i + 1}: picture ${p}`;
    });
  }

  protected renderPieces(y: number, width: number, maxHeight: number): number {
    const { cells, size, height } = gridCells(this.tiles.length, 3, width, 104, 8, 1, maxHeight);
    const values = this.offer?.setup?.values ?? [];
    const wins = winningTiles(this.game);
    const over = this.game.phase === 'over';
    const foil = this.deps.texture('road:scratch_foil');
    this.tiles.forEach((t, i) => {
      t.root.position.set(cells[i]!.x, y + cells[i]!.y);
      t.root.hitArea = { contains: (x: number, yy: number) => Math.abs(x) <= size / 2 && Math.abs(yy) <= size / 2 };
      const picture = tileAt(this.game, i);
      const won = wins.includes(i);
      t.well.setFloor(this.deps.floor());
      t.well.resize(size, size);
      t.well.position.set(-size / 2, -size / 2);
      t.ring.clear();
      if (won) t.ring.roundRect(-size / 2 - 1, -size / 2 - 1, size + 2, size + 2, 11).stroke({ color: COLOURS.gold, width: 3 });
      t.glow.visible = won;
      t.glow.width = t.glow.height = size * 1.7;
      // A win lights its three; the other tiles step back.
      t.root.alpha = over && wins.length > 0 && !won ? 0.6 : 1;
      t.root.cursor = canScratch(this.game, i) ? 'pointer' : picture !== null ? 'help' : 'default';
      if (picture !== null) {
        const tex = this.deps.picture(picture);
        if (tex) {
          t.picture.texture = tex;
          t.picture.scale.set(Math.min((size * 0.66) / tex.width, (size * 0.6) / tex.height));
        }
        t.picture.y = -size * 0.08;
        t.value.text = (values[picture] ?? 0).toLocaleString('en-GB');
        t.value.style = displayStyle(Math.max(12, Math.round(size * 0.17)));
        t.value.y = size * 0.32;
      }
      t.picture.visible = t.value.visible = picture !== null;
      // The foil: painted, or drawn while the art loads; scratched away once.
      if (foil) {
        t.foilArt.texture = foil;
        t.foilArt.width = t.foilArt.height = size - 6;
      }
      t.foilArt.visible = !!foil;
      t.foilDrawn.clear().roundRect(-size / 2 + 3, -size / 2 + 3, size - 6, size - 6, 8).fill({ color: 0xbfc3c8 });
      t.foilWord.style.fontSize = Math.max(10, Math.round(size * 0.12));
      if (picture !== null && !t.shown) {
        t.shown = true;
        void animate(260, (k) => {
          t.foil.alpha = 1 - k;
          t.foil.scale.set(1 + 0.12 * k);
        }).then(() => (t.foil.visible = false));
      } else if (picture === null) {
        t.shown = false;
        t.foil.visible = true;
        t.foil.alpha = 1;
        t.foil.scale.set(1);
      }
    });
    return y + height;
  }
}
